import React, { ReactNode, useRef, useState } from 'react';

import { Check, Copy } from 'lucide-react';

import { getIconPixels } from '@/theme/size-tokens';

export type CodeProps = {
  /** Code content to be displayed */
  children: ReactNode;
  /** Whether to render as a code block (pre + code) or inline code
   * @default false
   */
  block?: boolean;
  /** Visual style variant
   * @default 'default'
   */
  variant?: 'default' | 'primary' | 'muted';
  /** Show copy-to-clipboard button
   * @default false
   */
  copy?: boolean;
  /** Additional CSS classes for custom styling */
  className?: string;
};

/**
 * Code - Styled code snippets (inline or block)
 */
function Code({
  children,
  block = false,
  variant = 'default',
  copy = false,
  className = '',
}: Readonly<CodeProps>) {
  const codeRef = useRef<HTMLElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!codeRef.current) return;

    try {
      const textContent = codeRef.current.textContent || '';
      await navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const variantClasses = {
    default: 'bg-muted-active/50 text-foreground',
    primary: 'text-primary',
    muted: 'text-muted-foreground',
  };

  const baseClasses = `font-mono ${variantClasses[variant]}`;

  if (block) {
    const blockElement = (
      <pre
        className={`${baseClasses} border-border overflow-x-auto rounded-lg border p-4 ${className}`}
        ref={codeRef as React.RefObject<HTMLPreElement>}
      >
        <code>{children}</code>
      </pre>
    );

    if (copy) {
      return (
        <div className="group relative">
          {blockElement}
          <button
            type="button"
            onClick={handleCopy}
            className="bg-background/80 border-border hover:bg-muted-hover absolute top-2 right-2 inline-flex cursor-pointer items-center justify-center rounded border p-1.5 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 focus:opacity-100"
            aria-label="Copy to clipboard"
          >
            {copied ? (
              <Check
                size={getIconPixels('sm', 'desktop')}
                className="text-success transition-all"
              />
            ) : (
              <Copy
                size={getIconPixels('sm', 'desktop')}
                className="transition-all"
              />
            )}
          </button>
        </div>
      );
    }

    return blockElement;
  }

  const inlineElement = (
    <code
      ref={copy ? codeRef : undefined}
      className={`${baseClasses} rounded px-1.5 py-0.5 text-sm ${className}`}
    >
      {children}
    </code>
  );

  if (copy) {
    return (
      <span className="group inline-flex items-center gap-2">
        {inlineElement}
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex cursor-pointer items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
          aria-label="Copy to clipboard"
        >
          {copied ? (
            <Check
              size={getIconPixels('sm', 'desktop')}
              className="text-success transition-all"
            />
          ) : (
            <Copy
              size={getIconPixels('sm', 'desktop')}
              className="transition-all"
            />
          )}
        </button>
      </span>
    );
  }

  return inlineElement;
}

Code.displayName = 'Code';

export default Code;
